import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Wallet, Banknote, CreditCard, AlertCircle } from "lucide-react";

/**
 * Refund Modes Explained:
 * - store_credit_cash: Settles store credit, pays customer in CASH
 * - store_credit_card: Settles store credit, pays customer via CARD
 * - additional_cash: Additional payout (doesn't touch store credit, pays CASH)
 * 
 * All modes result in:
 * 1. Store credit balance decreases (or stays same for additional)
 * 2. Customer receives physical money
 * 3. Shop's cash/card decreases
 */

export default function IssueRefundDialog({ open, onOpenChange, customer, balance, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [refundMode, setRefundMode] = useState("store_credit_cash");
  const [shopId, setShopId] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);

  // Available store credit (what shop owes customer)
  const availableStoreCredit = balance?.payable || 0;

  useEffect(() => {
    if (open) {
      setAmount("");
      setReason("");
      setRefundMode("store_credit_cash");
      setShopId("");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
        if (data.items?.length === 1) {
          setShopId(data.items[0].id);
        }
      }).catch(() => setShops([]));
    }
  }, [open]);

  // Get payment mode from refund mode
  const getPaymentMode = () => {
    if (refundMode === "store_credit_cash" || refundMode === "additional_cash") return "cash";
    if (refundMode === "store_credit_card") return "card";
    return "cash";
  };

  // Get refund type for backend (store_credit vs cash)
  const getBackendRefundMode = () => {
    if (refundMode.startsWith("store_credit")) return "store_credit";
    return "cash";  // additional_cash is treated as cash payout
  };

  // Get human-readable label
  const getRefundLabel = () => {
    switch (refundMode) {
      case "store_credit_cash": return "Store Credit → Cash";
      case "store_credit_card": return "Store Credit → Card";
      case "additional_cash": return "Additional Payout (Cash)";
      default: return refundMode;
    }
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!shopId) return toast.error("Please select a shop");
    
    // Validate store credit refunds don't exceed available
    if (refundMode.startsWith("store_credit") && amt > availableStoreCredit + 0.01) {
      return toast.error(`Cannot refund ₹${amt.toFixed(2)} from store credit. Available: ₹${availableStoreCredit.toFixed(2)}`);
    }
    
    setBusy(true);
    try {
      await api.post(`/credit/${customer.id}/refund`, {
        amount: amt,
        shop_id: shopId,
        reason,
        refund_mode: getBackendRefundMode(),
        payment_mode: getPaymentMode(),
      });
      toast.success(`₹${amt.toFixed(2)} refunded (${getRefundLabel()})`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="issue-refund-dialog">
        <DialogHeader>
          <DialogTitle>Issue Refund</DialogTitle>
          <DialogDescription>
            Refund to <span className="font-medium">{customer?.name}</span>. Money flows OUT from shop to customer — this <strong>reduces</strong> what the shop owes the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Store Credit Available Info */}
          {availableStoreCredit > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <Wallet className="w-4 h-4" />
                <span>Available Store Credit: <strong>₹{availableStoreCredit.toFixed(2)}</strong></span>
              </div>
            </div>
          )}
          
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Store className="w-3 h-3" /> Shop Issuing Refund *
            </Label>
            <Select value={shopId} onValueChange={setShopId}>
              <SelectTrigger className="mt-1" data-testid="refund-shop-select">
                <SelectValue placeholder="Select shop issuing the refund" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              This refund will be recorded against the selected shop
            </p>
          </div>
          
          <div>
            <Label className="text-xs">Amount *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              placeholder="0.00"
              data-testid="refund-amount-input"
            />
          </div>
          
          <div>
            <Label className="text-xs">Refund Mode & Payment Method *</Label>
            <Select value={refundMode} onValueChange={setRefundMode}>
              <SelectTrigger className="mt-1" data-testid="refund-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {/* Store Credit Options - available only if customer has store credit */}
                {availableStoreCredit > 0 && (
                  <>
                    <SelectItem value="store_credit_cash">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Payout from Store Credit (CASH)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="store_credit_card">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                        <span>Payout from Store Credit (CARD)</span>
                      </div>
                    </SelectItem>
                  </>
                )}
                {/* Additional Payout - always available */}
                <SelectItem value="additional_cash">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-3.5 h-3.5 text-amber-600" />
                    <span>Additional Payout (CASH)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              {refundMode.startsWith("store_credit") 
                ? "Settles store credit balance & pays customer"
                : "Direct payout without affecting store credit"}
            </p>
          </div>
          
          {/* Warning for additional payout */}
          {refundMode === "additional_cash" && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>This is an additional payout that does NOT reduce existing store credit. Use this only for special cases.</span>
            </div>
          )}
          
          <div>
            <Label className="text-xs">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" rows={2} placeholder="e.g. damaged product, overcharge" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount || !shopId} variant="destructive" data-testid="refund-submit">
            {busy ? "Saving…" : "Issue Refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
